<script lang="ts" setup>
  import { informationCircle } from 'ionicons/icons'
  import { z } from 'zod'
  import ImageSplashForgotPassword from '~/assets/images/login/splash-forgot-password.png'

  const props = withDefaults(
    defineProps<{
      modalId?: string
      isView?: boolean
    }>(),
    {
      modalId: undefined,
      isView: false
    }
  )

  const vueRouter = useRouter()

  const inputElement = ref()
  const isSubmitting = ref(false)
  const successfulResetEmail = ref<string | undefined>()
  const apiErrorMessage = ref<string | undefined>()

  onMounted(() => {
    setTimeout(() => {
      if (!inputElement.value) {
        return
      }
      inputElement.value.$el.setFocus()
    }, 500)
  })

  function handleBackNavigation() {
    successfulResetEmail.value = undefined

    if (props.isView) {
      const routeFrom = vueRouter.options.history.state.back as string
      if (routeFrom) {
        vueRouter.back()
      } else {
        vueRouter.replace('/sign-in')
      }
    } else {
      modalController.dismiss(undefined, undefined, props.modalId)
    }
  }

  const validationSchema = toTypedSchema(
    z.object({
      email: z.string().min(1).email({ message: 'Your email must be valid and contain an @ in the address' })
    })
  )

  const { meta, handleSubmit } = useForm({
    validationSchema,
    initialValues: {
      email: undefined
    }
  })

  const isSubmitDisabled = computed(() => !meta.value.valid || !meta.value.dirty)

  const onSubmit = handleSubmit(async (values) => {
    try {
      apiErrorMessage.value = undefined
      isSubmitting.value = true

      await resetPassword(values.email)

      successfulResetEmail.value = values.email
    } catch (error) {
      console.error('Error resetting password', error)

      if (window.navigator.onLine) {
        apiErrorMessage.value = 'Huh, something went wrong. Try again Later'
      } else {
        apiErrorMessage.value = 'No internet connection.'
      }
    } finally {
      isSubmitting.value = false
    }
  })
</script>

<template>
  <GPageHeader :back-button-func="handleBackNavigation" disable-border disable-box-shadow title="Forgotten Password?" />

  <ion-content>
    <div class="page-content">
      <img :src="ImageSplashForgotPassword" alt="Forgot Password" class="hero-image" />

      <div v-if="apiErrorMessage" class="error-message-panel">
        <GIcon :icon="informationCircle" class="error-icon" color="var(--color-base-red)" size="16px" />
        <span>{{ apiErrorMessage }}</span>
      </div>

      <p>Let's bring back those benefits! Enter your email and we send you more deets.</p>

      <form @submit.prevent.stop="onSubmit">
        <GInputField ref="inputElement" :max-length="100" clear-input label="Email" name="email" type="email" />
      </form>
    </div>
  </ion-content>

  <ion-footer>
    <ion-toolbar>
      <GButton
        :disabled="isSubmitDisabled || isSubmitting"
        :should-show-loading-animation="isSubmitting"
        animation-color="light"
        block
        label="Reset Password"
        @click="onSubmit"
      />
    </ion-toolbar>
  </ion-footer>

  <GAlert
    :is-open="!!successfulResetEmail"
    :message="'We’ve sent password reset instructions to ' + (successfulResetEmail || 'your email')"
    header="Password Reset"
    @did-dismiss="handleBackNavigation"
  />
</template>

<style lang="scss" scoped>
  .page-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 16px;

    .hero-image {
      width: 100%;
      border-radius: 32px;
    }

    .error-message-panel {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 12px 16px;
      background: var(--color-secondary-red-lightest);
    }

    p {
      margin: 0;
    }
  }

  ion-footer {
    ion-toolbar {
      --padding-bottom: 30px;
      --padding-top: 0px;
    }
  }
</style>
